'use client';

import { useCallback, useEffect, useState } from 'react';
import { getText } from '@/utils/text/text';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useDebounce } from '@/hooks/use-debounce';
import './styles/@theme';

const LAYOUT_DEBOUNCE_MS = 150;
const GAME_LIST_SELECTORS = [
    '.virtual-games-grid__container',
    '.single-zone-component[data-zonelayout="game_list"] .grid',
    '.category-zones'
] as const;

const findGamesList = (): HTMLElement | null => {
    for (const selector of GAME_LIST_SELECTORS) {
        const element = document.querySelector<HTMLElement>(selector);
        if (element) {
            return element;
        }
    }
    return null;
};

export const BackToTopButton = () => {
    const [show, setShow] = useState(false);
    const { isMobile } = useGlobalContext();

    const updateShow = useCallback(() => {
        const { clientHeight, scrollHeight } = document.documentElement;
        const gamesList = findGamesList();

        if (gamesList) {
            const { top, height } = gamesList.getBoundingClientRect();
            const gamesBottomInDocument = top + window.scrollY + height;
            setShow(gamesBottomInDocument > clientHeight && !isMobile);
            return;
        }

        setShow(scrollHeight >= 2 * clientHeight && !isMobile);
    }, [isMobile]);

    const debouncedUpdateShow = useDebounce(updateShow, LAYOUT_DEBOUNCE_MS);

    useEffect(() => {
        const { documentElement, body } = document;
        updateShow();
        const resizeObserver = new ResizeObserver(updateShow);
        resizeObserver.observe(documentElement);
        resizeObserver.observe(body);
        for (const selector of GAME_LIST_SELECTORS) {
            const gamesList = document.querySelector<HTMLElement>(selector);
            if (gamesList) {
                resizeObserver.observe(gamesList);
            }
        }
        window.addEventListener('scroll', debouncedUpdateShow, { passive: true });
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', debouncedUpdateShow);
        }
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('scroll', debouncedUpdateShow);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', debouncedUpdateShow);
            }
        };
    }, [debouncedUpdateShow, updateShow]);

    const jumpToTop = () => {
        window.scrollTo({
            top: 0
        });
    };

    return (
        show && (
            <button type="button" onClick={jumpToTop} className="backtotop-button">
                {getText({ text: 'common.backToTop' })}
            </button>
        )
    );
};
